/**
 * HiddenComponentGhost
 *
 * Ghost representation of hidden components.
 * Shows dashed outline to maintain spatial context without cluttering the canvas.
 *
 * Features:
 * - Dashed border with reduced opacity
 * - Eye-off icon to indicate hidden state
 * - Component label shown when space permits
 * - Clickable to select (can still edit properties)
 * - Stays below visible components (z-index: 0)
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { EyeOff } from 'lucide-react';
import { useLabelLayoutStore } from './label-layout-store';
import { PX_PER_MM, COMPONENT_LABEL_KEYS, type ComponentConfig } from './label-layout-types';
import { cn } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

interface HiddenComponentGhostProps {
  config: ComponentConfig;
}

// ============================================================================
// COMPONENT
// ============================================================================

function HiddenComponentGhostInner({ config }: HiddenComponentGhostProps) {
  const { t } = useTranslation('settings');
  const scale = useLabelLayoutStore((s) => s.editor.scale);
  const selectedId = useLabelLayoutStore((s) => s.editor.selectedComponentId);
  const setSelectedComponent = useLabelLayoutStore((s) => s.setSelectedComponent);

  // Only render if component is hidden
  if (config.visible) return null;

  // Convert mm to pixels
  const x = config.position.x * PX_PER_MM * scale;
  const y = config.position.y * PX_PER_MM * scale;
  const width = config.size.width * PX_PER_MM * scale;
  const height = config.size.height * PX_PER_MM * scale;

  const isSelected = selectedId === config.id;

  return (
    <div
      className={cn(
        'absolute border-2 border-dashed rounded-sm',
        'flex flex-col items-center justify-center gap-1',
        'cursor-pointer transition-colors',
        isSelected
          ? 'border-primary/50 bg-primary/5'
          : 'border-muted-foreground/60 bg-muted/20 hover:border-muted-foreground/80'
      )}
      style={{
        left: x,
        top: y,
        width: width,
        height: height,
        zIndex: 0, // Below visible components
      }}
      onClick={(e) => {
        e.stopPropagation();
        setSelectedComponent(config.id);
      }}
      title={t('production.componentPanel.hiddenTitle', { name: t(COMPONENT_LABEL_KEYS[config.type]) })}
    >
      <EyeOff className="h-4 w-4 text-muted-foreground/50" />
      {height > 40 && (
        <span className="text-xs text-muted-foreground/50 truncate px-1">
          {t(COMPONENT_LABEL_KEYS[config.type])}
        </span>
      )}
    </div>
  );
}

// ============================================================================
// MEMOIZED EXPORT
// ============================================================================

/**
 * React.memo for performance optimization.
 * Only re-renders when config or selection changes.
 */
export const HiddenComponentGhost = React.memo(HiddenComponentGhostInner);

HiddenComponentGhost.displayName = 'HiddenComponentGhost';
